import React, {PropsWithChildren, useEffect} from 'react';
import { useTitle } from "fearless-common-ui";
import {Link, useSearchParams} from "react-router-dom";
import {ArrowRight} from "react-bootstrap-icons";
import {Row, Col} from "react-bootstrap";
import useAuth from "../../components/hooks/useAuth";
import SignUp from "../SignUp";
import {Pages} from "../../components/layout";
import Header from "../../components/Header";
import Description from "../../components/Description";
import {Course, Group, ShareRequest} from "fearless-common";
import ProfilePicture from "../../components/ProfilePicture";
import {routes} from "../../utils";
import Loading from "../../components/Loading";


/**
 * Shared layout for "shareable link" landing pages — pages a user lands on
 * after clicking an invite link (e.g. join a group, join a course).
 *
 * Behaviour at a glance:
 *   - While `isLoading` is true: only the header and a spinner are shown.
 *   - When `error` is set: the user is redirected to {@link routes.invalidLink}
 *     with the error string passed via the `?message=` query param.
 *   - When the visitor is **logged in**: a two-column layout is rendered with
 *     the visitor's profile on the left and the page-specific content
 *     (`children` — typically an "accept invitation" CTA) on the right.
 *     A right-arrow separator is shown between the columns unless `arrow`
 *     is set to `false`.
 *   - When the visitor is **anonymous**: the {@link SignUp} flow is rendered
 *     instead, pre-wired with `shareRequests`, `group`/`course` context and a
 *     post-signup redirect back to `currentPageUrl`.
 *
 * The optional `description` component receives the inviter's first name
 * (read from the `?userFirstName=` query param), letting callers personalise
 * the copy (e.g. "Jane has invited you to join …").
 */
export default function ShareableLinkPage({
    header,
    tabTitle,
    description: DescriptionComponent,
    currentPageUrl,
    children,
    isLoading,
    error,
    shareRequests = undefined,
    group = undefined,
    course = undefined,
    arrow = true,
}: PropsWithChildren<{
    /** Large heading shown at the top of the page (both auth states). */
    header: string;
    /** Browser tab title — passed straight to {@link useTitle}. */
    tabTitle: string;
    /**
     * Optional component rendered under the header when the visitor is
     * logged in AND the URL contains a `?userFirstName=` query param. The
     * inviter's first name is forwarded as `user.first_name` so the caller
     * can write personalised copy such as "{first_name} has invited you…".
     */
    description?: React.ComponentType<{ user: { first_name: string } }>;
    /**
     * URL of the current page. Used to build the post-logout / post-login /
     * post-signup redirect targets so the user is returned here after
     * authenticating.
     */
    currentPageUrl: string;
    /**
     * When true (default), shows a right-arrow column between the visitor's
     * profile and the page content. Set to `false` to render a thin border
     * separator instead — useful for denser layouts.
     */
    arrow?: boolean;
    /** Pending share requests forwarded to the SignUp flow for anonymous visitors. */
    shareRequests?: ShareRequest[];
    /** Group context for the invite (mutually exclusive with `course`). */
    group?: Group;
    /** Course context for the invite (mutually exclusive with `group`). */
    course?: Course;
    /**
     * If set, the page redirects to the invalid-link route with this string
     * as the user-visible message. Use for "link expired", "already used", …
     */
    error?: string;
    /** While true, only the header and a loading spinner are rendered. */
    isLoading?: boolean;
}>) {
    useTitle(tabTitle);
    const { user } = useAuth();

    const [sp] = useSearchParams();
    const userFirstName =  sp?.get('userFirstName');

    useEffect(() => {
        if (error) {
            const url = new URL(window.location.origin + routes.invalidLink);
            url.searchParams.set('message', error);
            window.location.href = url.toString();
        }
    }, [error]);

    const headerComponent = <Header component="h1" className="text-center mb-0 pb-0">{header}</Header>;
    if (isLoading) return <Pages.Centred width="xs" padded>{headerComponent}<Loading /></Pages.Centred>;

    return user ? (
        <Pages.Centred width={group ? "xs" : 'sm'}>
            <span className="d-block mb-5">
                {headerComponent}
                {userFirstName && DescriptionComponent && (
                    <Description className="text-center mt-0">
                        <DescriptionComponent user={{ first_name: userFirstName }} />
                    </Description>
                )}
            </span>
            <Row className="align-items-start justify-content-center">
                <Col
                  className={`text-center ${arrow ? '': 'border-1 border-end border-gray'}`}
                  sm={group ? 6 : 5}
                >
                    <ProfilePicture user={user} size={100} />
                    <h2>{user?.first_name} {user?.last_name}</h2>

                    <Description className="mb-5 text-sm">
                        Is this not you?
                        <br />
                        <Link to={routes.logoutWithRedirect(currentPageUrl)}>Sign up</Link>
                        &nbsp;or log in via your account&nbsp;
                        <Link to={routes.loginAgain(currentPageUrl)}>here</Link>
                    </Description>
                </Col>
                {arrow && <Col xs={1} className="pt-5 mt-5"><ArrowRight size={40} /></Col>}

                <Col className="text-center col-sm-5 px-4">
                    {children}
                </Col>
            </Row>
        </Pages.Centred>
    ) : (
        <SignUp
            shareRequests={shareRequests}
            group={group}
            course={course}
            redirect={currentPageUrl}
        />
    );
}
